import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2, ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Logo } from "@/components/layout/Logo";
import {
  authClient,
  REDIRECT_URI,
  popPostLoginRedirect,
  saveTokens,
  takeChallenge,
} from "@/lib/auth-client";

export const Route = createFileRoute("/auth/callback")({
  component: AuthCallbackPage,
  validateSearch: (search: Record<string, unknown>) => ({
    code: typeof search.code === "string" ? search.code : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
  }),
});

/**
 * OAuth2 PKCE callback. The OpenAuth issuer redirects here with `?code=...`
 * after a successful WhatsApp OTP flow. We exchange the code for an
 * access/refresh token pair, persist it, and forward the user to wherever
 * they were trying to go (or `/dashboard` by default).
 */
function AuthCallbackPage() {
  const { code, error: oauthError } = Route.useSearch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (oauthError) {
      setError(oauthError);
      return;
    }
    if (!code) {
      setError("missing_code");
      return;
    }
    const challenge = takeChallenge();
    if (!challenge) {
      setError("missing_challenge");
      return;
    }
    void (async () => {
      try {
        const exchanged = await authClient.exchange(
          code,
          REDIRECT_URI,
          challenge.verifier,
        );
        if (exchanged.err) {
          setError(String(exchanged.err));
          return;
        }
        saveTokens({
          access: exchanged.tokens.access,
          refresh: exchanged.tokens.refresh,
        });
        const target = popPostLoginRedirect() ?? "/dashboard";
        // Hard reload so AuthProvider re-runs verify with fresh tokens.
        window.location.replace(target);
      } catch (e) {
        setError(e instanceof Error ? e.message : "unknown_error");
      }
    })();
  }, [code, oauthError]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <Card className="w-full max-w-sm">
        <CardHeader className="items-center text-center">
          <Logo />
          <CardTitle className="pt-2">
            {error
              ? t("auth.callback_error_title")
              : t("auth.callback_working_title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4 text-center">
          {error ? (
            <>
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
                <ShieldAlert className="h-6 w-6" />
              </div>
              <p className="text-sm text-muted-foreground">
                {t("auth.callback_error_body")}
              </p>
              <Button
                onClick={() =>
                  navigate({ to: "/login", search: { reason: "error" } })
                }
              >
                {t("auth.signin_cta")}
              </Button>
            </>
          ) : (
            <>
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
              <p className="text-sm text-muted-foreground">
                {t("auth.callback_working_body")}
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
